import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import i18next from 'i18next';
import * as Sentry from '@sentry/react';
import { AuthAPI, resetSessionExpiredFlag } from '../services/auth';
import {
  getTokenExpiry,
  decodeTokenPayload,
  getStoredGuestEntryKey,
  storeGuestEntryKey,
  getAuthStorageFailure,
} from '../services/authFetch.js';
import { getLandingAudience, getEntryScenarioId, getGuestEntryKey } from '../lib/landingAudience';
import { decideGuestOrgReuse, guestOrgEntryCacheKey } from '../lib/auth/guestOrgMismatch';
// #3530: imported for its side effect too (captures referrer + utm_* at boot,
// like landingAudience), and for the snapshot `loginAsGuest` sends.
import { getEntrySource } from '../lib/entrySource';
// #4044: boot-time capture of the Yoma link context (evaluates before React mounts).
import '../lib/yomaEntry';
// #3842 follow-up: boot-time capture of the link's `utm_content` entry code,
// sent on POST /api/runs as `entryCode` (runs.entry_code).
import '../lib/entryCode';
import { posthog } from '../main.jsx';
import { clearClaimMapSession } from '../utils/claimMapSession';
import { formatDateForLanguage } from '../utils/formatDateForLanguage';

const AuthContext = createContext();

/**
 * D-121 (#2725) — the one prompt: "is this unfinished conversation yours?"
 *
 * Exported (not inlined in loginWithGoogle below) so a test can assert on
 * the exact copy without stubbing `window.confirm` itself, and so the
 * message-building logic is checkable independent of the browser dialog.
 *
 * Deliberately shows a date and the SCENARIO TITLE only — never the
 * learner's own typed messages. A stranger holding someone else's guest
 * token must not be shown what that person actually said (the Navigator's
 * conversations have included crisis disclosures) in order to decide
 * whether to claim the session; the scenario title is scripted persona
 * content, not the learner's words, and identifies the session without
 * that exposure.
 *
 * Fails toward "no" (declines, starts a clean account) when there is no
 * `window.confirm` available (non-browser test/SSR context) or no preview
 * data at all — the safer default when we cannot ask is to NOT silently
 * bind a stranger's session, matching the whole point of this decision.
 *
 * @param {{guest_created_at?: string, preview?: {scenario_title?: string|null, started_at?: string}|null}|null} pending
 * @returns {boolean} true = "yes, it's mine" (keep history); false = "no" (start fresh)
 */
export function confirmGuestConversion(pending) {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return false;

  const startedAt = pending?.preview?.started_at || pending?.guest_created_at || null;
  const dateLabel = startedAt ? formatDateForLanguage(new Date(startedAt), i18next.resolvedLanguage || i18next.language) : 'earlier';
  const scenarioTitle = pending?.preview?.scenario_title || null;
  const whatLabel = scenarioTitle ? ` (“${scenarioTitle}”)` : '';

  const message = `We found an unfinished practice session on this device from ${dateLabel}${whatLabel}. ` +
    `Is this your session? Choose OK to keep it, or Cancel to start a new account instead.`;

  return window.confirm(message);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * #3406 — the ONE explicit auth state. Before this, `isLoading` and
 * `isAuthenticated` were two independent booleans, and a failed restore left
 * them saying "logged out" while a usable token stayed in storage — so the
 * next guest entry could mint a guest token over a real learner's session.
 *
 *   loading        boot restore in progress (nothing decided yet)
 *   authenticated  a real (non-guest) user, verified by /auth/me or a login
 *   guest          a guest session, verified the same way
 *   degraded       a token is stored but could NOT be verified (network,
 *                  5xx, 429 on /auth/me). Grants NO privileges
 *                  (`isAuthenticated` is false); a bounded retry runs in the
 *                  background; a definitive 401 ends it at once; the learner
 *                  always has an exit (retry now, or sign in again).
 *   anonymous      no usable token
 */
export const AUTH_STATUS = Object.freeze({
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  GUEST: 'guest',
  DEGRADED: 'degraded',
  ANONYMOUS: 'anonymous',
});

// Bounded background retry for a degraded restore: three more attempts over
// ~22s, then stop and leave the learner's own Retry / Sign in again. Never
// unbounded — a dead token behind a 5xx must not be kept alive forever.
export const RESTORE_RETRY_DELAYS_MS = Object.freeze([2000, 5000, 15000]);

// #3459 Q2881-1: how many times one Google sign-in may put the "is this your
// session?" prompt in front of the learner. A second ask is legitimate — the
// 10-minute confirm token can expire while the first dialog is open, and the
// server then asks again with a fresh one. A third means the answer cannot
// be applied; stop rather than loop.
export const MAX_GUEST_CONVERSION_PROMPTS = 2;

// #3578 plan check design change 4: the most a boot restore waits for
// /auth/me before calling the session DEGRADED (network-shaped, never a 401).
// Without a bound, a request that never settles kept the app on LOADING until
// the browser's own fetch timeout, which on mobile can be minutes.
export const RESTORE_REQUEST_TIMEOUT_MS = 12000;

const AUTH_TOKEN_STORAGE_KEY = 'chatbot_auth_token';

// #3636 item 3: the size of the throwaway value probed before a FRESH guest
// mint on a full store. A guest JWT is a few hundred bytes (id, role,
// is_guest, org, iat, exp); 1 KB leaves headroom, so a pass means the new
// token will fit.
const GUEST_TOKEN_PROBE_BYTES = 1024;

function codedError(message, code) {
  const err = new Error(message);
  err.code = code;
  return err;
}

// Telemetry must never break auth: a throwing or absent PostHog is ignored.
function captureAuthEvent(name, properties) {
  try {
    if (posthog && typeof posthog.capture === 'function') posthog.capture(name, properties);
  } catch {
    // best-effort
  }
}

/**
 * #3578: the failure class of a restore that ended DEGRADED, for the
 * `auth_restore_degraded` event. A closed set, so a reader's GROUP BY is
 * readable: timeout | network (status 0) | rate_limited (429) |
 * server_error (5xx) | http_other (any other status) | unknown (no status).
 */
export function classifyRestoreFailure(error) {
  if (error?.code === 'RESTORE_TIMEOUT') return 'timeout';
  const status = error?.status;
  if (status === 0) return 'network';
  if (status === 429) return 'rate_limited';
  if (typeof status === 'number' && status >= 500) return 'server_error';
  if (typeof status === 'number') return 'http_other';
  return 'unknown';
}

// Races the /auth/me restore request against RESTORE_REQUEST_TIMEOUT_MS. On
// the timeout it aborts the fetch (when it honours the signal) and rejects
// with a coded RESTORE_TIMEOUT whatever the request does, so the restore
// always reaches a terminal state.
function withRestoreTimeout(run, ms) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer = null;
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      try { controller?.abort(); } catch { /* ignore */ }
      reject(codedError('The sign-in check timed out.', 'RESTORE_TIMEOUT'));
    }, ms);
  });
  return Promise.race([Promise.resolve().then(() => run(controller?.signal)), timeout])
    .finally(() => clearTimeout(timer));
}

// A stored token that is a real (non-guest), unexpired session.
function isUsableRealSessionToken(token) {
  return !!token && !AuthAPI.isTokenExpired(token) && decodeTokenPayload(token)?.is_guest !== true;
}

// #3642: after the setting is turned off, Safari keeps the block for the open
// browser session (device evidence: neither Try again nor a same-tab reload
// cleared it; quitting and reopening Safari did), so this must not say
// "reload". Keep it identical to the en `auth.storageBlocked` value; the
// default is what shows when i18n has not loaded.
const STORAGE_BLOCKED_MESSAGE =
  'Your browser is blocking the storage this site needs to keep you signed in. '
  + 'Allow cookies and website data for this site (on iPhone: Settings > Safari, turn off Block All Cookies), then quit and reopen your browser.';

const STORAGE_FULL_MESSAGE =
  "Your browser's storage for this site is full, so a new session cannot be kept. "
  + "Clear this site's website data in your browser settings, then reload the page.";

// #3578 (Gilbert, 2026-09-29): a browser whose storage throws gets a clear
// stop, not an in-memory session. Reported twice, because PostHog itself
// relies on storage and so undercounts exactly these visitors: a PostHog
// `auth_restore_degraded` event with failure_class 'storage_blocked', and a
// Sentry warning `auth_storage_blocked`. `surface` says where it was hit.
// #3636 item 3: `reason` 'quota' (storage full, not blocked) keeps the same
// code and event, adds `storage_failure: 'quota'` to both, and says "full"
// rather than "blocked" in the message.
function storageBlockedError(surface, cause, reason = null) {
  captureAuthEvent('auth_restore_degraded', {
    failure_class: 'storage_blocked',
    surface,
    ...(reason ? { storage_failure: reason } : {}),
  });
  try {
    Sentry.captureMessage('auth_storage_blocked', {
      level: 'warning',
      tags: { surface, ...(reason ? { storage_failure: reason } : {}) },
    });
  } catch {
    // best-effort
  }
  let message = reason === 'quota' ? STORAGE_FULL_MESSAGE : STORAGE_BLOCKED_MESSAGE;
  try {
    if (i18next.isInitialized) {
      message = reason === 'quota'
        ? i18next.t('auth.storageFull', { defaultValue: STORAGE_FULL_MESSAGE })
        : i18next.t('auth.storageBlocked', { defaultValue: STORAGE_BLOCKED_MESSAGE });
    }
  } catch {
    // keep the English default
  }
  const err = codedError(message, 'STORAGE_UNAVAILABLE');
  if (cause) err.cause = cause;
  return err;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.LOADING);
  // #3578: a synchronous mirror of authStatus. restoreSession returns the
  // terminal status to its caller (Start Practice reads the RETURNED value,
  // never a render-time closure), and the storage listener reads the current
  // status without re-subscribing on every change. Every write goes through
  // setStatus so the two never disagree.
  const authStatusRef = useRef(AUTH_STATUS.LOADING);
  const setStatus = useCallback((status) => {
    authStatusRef.current = status;
    setAuthStatus(status);
  }, []);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  // #3636 item 2: the token the current AUTHENTICATED/GUEST state was
  // established with (a restore's verified token, or login()'s), and the id
  // of the user that state shows. A storage event whose stored token equals
  // the token is not a change of session. Both are written synchronously
  // next to setUser/setStatus (never by an effect, which would lag a render
  // and let a storage event in that gap read a stale id).
  const verifiedTokenRef = useRef(null);
  const verifiedUserIdRef = useRef(null);
  // #3753: the organization of the user that state shows, written next to
  // verifiedUserIdRef. loginAsGuest compares a verified guest's org with the
  // org a fresh mint for the current entry would land in.
  const verifiedUserOrgIdRef = useRef(null);
  // #3753: entry ({audience, scenarioId}) -> predicted guest org id, for this
  // page's lifetime. Filled by a successful prediction AND by every mint (the
  // mint's own org IS the answer for its entry), so the re-render after a mint
  // does not ask the server again.
  const guestOrgPredictionRef = useRef(new Map());
  // #3636 item 4: the token the restore that ended DEGRADED read. "Sign in
  // again" on the degraded panel ends THAT session, not whatever storage
  // holds by click time. State, not a ref: the panel reads it at render.
  const [degradedToken, setDegradedToken] = useState(null);
  // mega117 TP-3636-2: the token THIS tab established itself, either read by
  // the mount restore or written by this tab's login(). A token that reached
  // this tab any other way (another tab's write, picked up by the storage
  // listener or by a restore's superseded check) is never "own". Only an own
  // token may become degradedToken, so "Sign in again" can never revoke a
  // session this tab adopted from another tab: with that token stored and
  // degradedToken null, logout({ expectedToken }) re-checks instead.
  const ownTokenRef = useRef(null);
  const isLoading = authStatus === AUTH_STATUS.LOADING;
  const isAuthenticated = authStatus === AUTH_STATUS.AUTHENTICATED || authStatus === AUTH_STATUS.GUEST;
  const restoreRetryTimer = useRef(null);
  // PR #3543 review-fix: every restore attempt takes a generation number,
  // and only the newest generation may write auth state. Cancelling a timer
  // does not cancel an attempt already in flight, and two attempts carry the
  // SAME stored token, so the token check alone cannot tell a stale attempt
  // from a current one: an older background retry failing after the
  // learner's successful "Try again" used to put them back on 'degraded'.
  const restoreGeneration = useRef(0);

  // Cancels the scheduled retry AND supersedes any attempt in flight. Every
  // explicit auth decision (login, logout, session-expired, Try again,
  // unmount) goes through here, so none can be overwritten by a late restore.
  const cancelRestoreRetry = useCallback(() => {
    restoreGeneration.current += 1;
    if (restoreRetryTimer.current) {
      clearTimeout(restoreRetryTimer.current);
      restoreRetryTimer.current = null;
    }
  }, []);

  // Handle session expiration - clear state and prepare for redirect
  const handleSessionExpired = useCallback((message) => {
    console.log('🔒 Session expired, logging out user');
    cancelRestoreRetry();
    verifiedTokenRef.current = null;
    verifiedUserIdRef.current = null;
    verifiedUserOrgIdRef.current = null;
    ownTokenRef.current = null;
    setUser(null);
    setStatus(AUTH_STATUS.ANONYMOUS);
    setSessionExpiredMessage(message || 'Your session has expired. Please log in again.');
  }, [cancelRestoreRetry, setStatus]);

  // Clear the session expired message (called after displaying it)
  const clearSessionExpiredMessage = useCallback(() => {
    setSessionExpiredMessage(null);
  }, []);

  // Listen for session expired events from authFetch
  useEffect(() => {
    const handleSessionExpiredEvent = (event) => {
      handleSessionExpired(event.detail?.message);
    };

    window.addEventListener('sessionExpired', handleSessionExpiredEvent);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpiredEvent);
    };
  }, [handleSessionExpired]);

  // #3406: restore (and re-verify) the stored session. Every outcome is
  // conditional on the token this attempt STARTED with still being the one
  // in storage (mega115 plan check finding 2): a login, logout or another
  // tab's write that lands while /auth/me is in flight supersedes this
  // attempt, and a stale attempt must never overwrite, clear or downgrade
  // what replaced it.
  //
  // #3578 L3-R2: EVERY path ends in a terminal state, and the function
  // RESOLVES to that state (authenticated | guest | degraded | anonymous) so a
  // caller can act on the verdict instead of a render-time closure (L3-R3/J,
  // Start Practice). The paths, each pinned in authRestoreTerminal3578.test.jsx:
  //   no readable token (incl. storage that throws)  -> anonymous
  //   token expired client-side                       -> anonymous (cleared)
  //   /auth/me 200                                    -> authenticated | guest
  //   /auth/me 401                                    -> anonymous (cleared)
  //   /auth/me other failure / never settles          -> degraded (timeout bound)
  //   superseded by a same-tab writer                 -> that writer's state
  //   token swapped/removed by another tab            -> re-restore / anonymous
  //   anything that throws unexpectedly               -> degraded if a token
  //                                                      was read, else anonymous
  // The last row is the try/finally the old initAuth had and #3543 dropped;
  // it encloses the request's own catch, whose superseded() check reads storage.
  //
  // #3599: `via` says what started this attempt, for the `auth_restore_recovered`
  // event a degraded restore that settles verified emits: 'retry_click' (any
  // learner-initiated retryAuthRestore(): the panel's "Try again" AND Start
  // Practice's verification, startPracticeGate) or 'background' (everything else: the scheduled
  // retries, another tab's write picked up by the storage listener, and the
  // re-check logout() runs for a token it does not own).
  const restoreSession = useCallback(async ({ attempt = 0, via = 'background' } = {}) => {
    restoreRetryTimer.current = null;
    const generation = ++restoreGeneration.current;
    const isCurrent = () => generation === restoreGeneration.current;
    let settled = false;
    let token = null;

    // Writes this attempt's terminal state, but only while it is still the
    // newest attempt (the generation check the fallback below also needs).
    // A superseded attempt returns whatever state its superseder wrote.
    //
    // #3636 item 1: a VERIFIED outcome (authenticated | guest) is a fresh
    // session exactly as login() is, so it resets what login() resets:
    // authFetch's session-expired latch (else the NEXT expiry of this adopted
    // session dispatches nothing and the tab 401s silently) and any shown
    // expiry message. Only after the isCurrent() check, so a superseded
    // attempt resets nothing, and never on degraded/anonymous.
    const settle = (status, nextUser = null) => {
      settled = true;
      if (!isCurrent()) return authStatusRef.current;
      const verified = status === AUTH_STATUS.AUTHENTICATED || status === AUTH_STATUS.GUEST;
      // #3599: read BEFORE the write below. One event per recovery out of a
      // degraded state (never for a boot restore, whose prior status is
      // LOADING). Not for a recovery through loginAsGuest's re-check (that is
      // login(), not a restore): the recovery rate this stream gives is a
      // lower bound.
      const recoveredFromDegraded = verified && authStatusRef.current === AUTH_STATUS.DEGRADED;
      verifiedTokenRef.current = verified ? token : null;
      verifiedUserIdRef.current = verified ? (nextUser?.id ?? null) : null;
      verifiedUserOrgIdRef.current = verified ? (nextUser?.organization_id ?? null) : null;
      // TP-3636-2: only this tab's own token; see ownTokenRef.
      setDegradedToken(status === AUTH_STATUS.DEGRADED && token === ownTokenRef.current ? token : null);
      setUser(nextUser);
      setStatus(status);
      if (verified) {
        resetSessionExpiredFlag();
        setSessionExpiredMessage(null);
      }
      if (recoveredFromDegraded) {
        captureAuthEvent('auth_restore_recovered', { via, is_guest: status === AUTH_STATUS.GUEST });
      }
      return status;
    };

    // Any change to the stored token since this attempt started supersedes
    // it — INCLUDING removal to null (logout, another tab, authFetch's
    // clear-on-401). A same-tab writer (login()) already bumped the
    // generation and set state itself, so it owns the outcome. A removal
    // leaves storage empty, so the only truthful state is anonymous (and no
    // retry). PR #3543 review-fix: the catch used `current && current !==
    // token`, false for null, so a logout during an in-flight restore was
    // undone by its late failure (degraded again, and another retry
    // scheduled).
    //
    // A REPLACEMENT with the generation unchanged is different: nothing in
    // this tab wrote it, because every in-tab writer (login/logout/Try
    // again) bumps the generation before or as it sets state. The only
    // source left is another tab writing localStorage directly. Re-run
    // restoreSession() so the new token gets checked. That new attempt takes
    // the next generation, so this can fire at most once per actual token
    // swap: its own check compares against whatever is in storage by then,
    // which is this same token unless it changes again.
    //
    // Returns null when this attempt is still current and its token still
    // stored; otherwise the outcome (a status, or the re-restore's promise).
    const supersededOutcome = () => {
      if (!isCurrent()) {
        settled = true;
        return authStatusRef.current;
      }
      const current = AuthAPI.getStoredToken();
      if (current === token) return null;
      if (!current) return settle(AUTH_STATUS.ANONYMOUS);
      settled = true;
      return restoreSession();
    };

    try {
      token = AuthAPI.getStoredToken();
      if (!token) return settle(AUTH_STATUS.ANONYMOUS);

      // Proactive client-side expiry check — skip the network call entirely
      if (AuthAPI.isTokenExpired(token)) {
        AuthAPI.clearStoredToken();
        return settle(AUTH_STATUS.ANONYMOUS);
      }

      let userData;
      try {
        userData = await withRestoreTimeout(
          (signal) => AuthAPI.getCurrentUser({ signal }),
          RESTORE_REQUEST_TIMEOUT_MS,
        );
      } catch (error) {
        console.warn('Failed to restore authentication:', error);
        const supersededResult = supersededOutcome();
        if (supersededResult !== null) return supersededResult;

        // #3011: only a genuine rejection of the token (401 from /auth/me)
        // means the token is actually invalid. `AuthAPI.getCurrentUser()`
        // also throws here for a network-shaped failure — a cancelled fetch
        // from a full page navigation that starts before the request
        // resolves surfaces as `FetchError{status: 0}`
        // (utils/safeFetch.ts `wrapNetworkErrorOrRethrow`), and the same
        // shape covers 5xx/429/offline, and #3578's RESTORE_TIMEOUT. None of
        // those are evidence the token is bad. A revoked, deactivated or
        // deleted user gets a 401 here (middleware/auth.js TOKEN_REVOKED /
        // REAUTH_REQUIRED), so a dead token ends at once and is never kept
        // alive by the degraded state.
        if (error?.status === 401) {
          AuthAPI.clearStoredToken(); // still this attempt's token (supersededOutcome() checked)
          return settle(AUTH_STATUS.ANONYMOUS);
        }

        // #3406: ambiguous failure. Keep the token, grant nothing, say so
        // explicitly (`degraded`, not a silent "logged out"), and retry on a
        // bounded schedule. Before this, the catch set no state at all: the
        // UI read as signed out while the token stayed, and the next guest
        // entry minted a guest token over it.
        const status = settle(AUTH_STATUS.DEGRADED);
        // #3578 class A: the instrument the groom found missing. One event
        // per attempt that ends degraded (attempt 0 is the boot restore).
        // is_guest is the token's own claim, not PII.
        captureAuthEvent('auth_restore_degraded', {
          failure_class: classifyRestoreFailure(error),
          attempt,
          is_guest: decodeTokenPayload(token)?.is_guest === true,
        });
        if (isCurrent() && attempt < RESTORE_RETRY_DELAYS_MS.length) {
          restoreRetryTimer.current = setTimeout(
            () => { restoreSession({ attempt: attempt + 1 }); },
            RESTORE_RETRY_DELAYS_MS[attempt],
          );
        }
        return status;
      }

      const supersededResult = supersededOutcome();
      if (supersededResult !== null) return supersededResult;
      const status = settle(
        userData.user?.is_guest ? AUTH_STATUS.GUEST : AUTH_STATUS.AUTHENTICATED,
        userData.user,
      );

      // Identify returning user in PostHog
      if (posthog && userData.user) {
        try {
          posthog.identify(userData.user.id, {
            /* pii-ok: person-profile identification is what identify() is for — email is the
               join key against partner cohort lists and support requests. Deliberate, not a
               payload leak. Do NOT copy this marker onto capture() event properties (#966). */
            email: userData.user.email,
            role: userData.user.role,
            name: userData.user.name,
          });
        } catch {
          // analytics must not undo a verified session
        }
      }
      return status;
    } catch (unexpected) {
      console.error('[AUTH_RESTORE] unexpected failure; ending the restore in a terminal state', unexpected);
    } finally {
      // Reached with `settled` false only on an unexpected throw (e.g. a
      // malformed /auth/me body). Never leave LOADING behind: a token that
      // was read stays, unverified (degraded); none means anonymous. Guarded
      // by the generation, so a superseded attempt writes nothing.
      if (!settled && isCurrent()) {
        verifiedTokenRef.current = null;
        verifiedUserIdRef.current = null;
        verifiedUserOrgIdRef.current = null;
        setDegradedToken(token && token === ownTokenRef.current ? token : null);
        setUser(null);
        setStatus(token ? AUTH_STATUS.DEGRADED : AUTH_STATUS.ANONYMOUS);
      }
    }
    return authStatusRef.current;
  }, [setStatus]);

  // Check for existing authentication on mount
  useEffect(() => {
    // The token this tab boots with is its own session (TP-3636-2).
    ownTokenRef.current = AuthAPI.getStoredToken();
    restoreSession();
    return () => cancelRestoreRetry();
  }, [restoreSession, cancelRestoreRetry]);

  // #3580 sibling (7a): another tab's sign-in, sign-out or guest mint.
  // localStorage fires 'storage' only in OTHER tabs, so without this a tab on
  // the degraded panel (or an anonymous tab, #3578 J) never learned that the
  // session it needs now exists, and stayed stuck until a manual reload.
  // The re-check is a restore, which never clears a token on anything but a
  // 401 for that same token.
  //
  // #3636 item 2: a VERIFIED tab re-checks too. There is one token slot, so
  // after another tab signs in as B every request from this tab carries B's
  // token; keeping A's state meant a Start Practice here created B's run
  // while the tab showed A. The listener reads STORAGE, not event.newValue:
  //   - the stored token is the one this state was verified with: no change
  //     (e.g. a stale event), nothing to do;
  //   - storage is empty (another tab signed out): left to the 401 path,
  //     authFetch.js clears and raises sessionExpired on the next request;
  //   - a different token for a DIFFERENT user (the decoded `id` claim vs the
  //     shown user.id): drop A synchronously, BEFORE the restore's await, so
  //     the tab never shows A while its requests carry B's token, then
  //     restore (which adopts B, or ends anonymous/degraded);
  //   - a different token for the same user (a re-login elsewhere): restore,
  //     keeping the page mounted.
  // No loop: a restore never writes a token, only clears one on a 401 or
  // client-side expiry, and an empty slot is ignored while verified.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (event) => {
      // key === null means another tab called localStorage.clear().
      if (event.key !== null && event.key !== AUTH_TOKEN_STORAGE_KEY) return;
      const status = authStatusRef.current;
      if (status === AUTH_STATUS.AUTHENTICATED || status === AUTH_STATUS.GUEST) {
        const stored = AuthAPI.getStoredToken();
        if (!stored || stored === verifiedTokenRef.current) return;
        const nextId = decodeTokenPayload(stored)?.id;
        const shownId = verifiedUserIdRef.current;
        if (nextId == null || shownId == null || String(nextId) !== String(shownId)) {
          verifiedTokenRef.current = null;
          verifiedUserIdRef.current = null;
          verifiedUserOrgIdRef.current = null;
          setUser(null);
          setStatus(AUTH_STATUS.LOADING);
        }
      } else if (status !== AUTH_STATUS.DEGRADED && status !== AUTH_STATUS.ANONYMOUS) {
        // LOADING: the attempt in flight compares storage with the token it
        // started with before settling, and re-restores on a swap.
        return;
      }
      cancelRestoreRetry();
      restoreSession({ attempt: RESTORE_RETRY_DELAYS_MS.length });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [restoreSession, cancelRestoreRetry, setStatus]);

  /**
   * #3406: the learner's own "try again" from the degraded state. One
   * immediate attempt; it does not restart the background schedule.
   *
   * #3578 L3-R3/J: resolves to the TERMINAL status of that attempt
   * (authenticated | guest | degraded | anonymous). Start Practice verifies
   * with it before POST /api/runs and must read this returned value: a
   * handler's `authStatus` is the closure from its render, which is stale
   * after the await.
   */
  const retryAuthRestore = useCallback(async () => {
    cancelRestoreRetry();
    return restoreSession({ attempt: RESTORE_RETRY_DELAYS_MS.length, via: 'retry_click' });
  }, [cancelRestoreRetry, restoreSession]);

  const login = async (token, userData) => {
    cancelRestoreRetry();
    try {
      // #3636 item 3: reusing the guest token that is ALREADY stored writes
      // nothing, so a full (quota) store can still resume that session. A
      // blocked store reads as null here, so it still reaches storeToken and
      // its STORAGE_UNAVAILABLE throw.
      if (AuthAPI.getStoredToken() !== token) AuthAPI.storeToken(token);
    } catch (err) {
      // #3578: the session cannot be kept in this browser. Stop with a
      // coded, translated error; never report a session that is not stored.
      if (err?.code === 'STORAGE_UNAVAILABLE') throw storageBlockedError('login', err);
      throw err;
    }
    verifiedTokenRef.current = token;
    verifiedUserIdRef.current = userData?.id ?? null;
    verifiedUserOrgIdRef.current = userData?.organization_id ?? null;
    ownTokenRef.current = token;
    setDegradedToken(null);
    setUser(userData);
    setStatus(userData?.is_guest ? AUTH_STATUS.GUEST : AUTH_STATUS.AUTHENTICATED);
    setSessionExpiredMessage(null); // Clear any expired message
    resetSessionExpiredFlag(); // Reset the flag so future 401s are detected

    // Identify user in PostHog for analytics
    if (posthog && userData) {
      posthog.identify(userData.id, {
        /* pii-ok: same deliberate person-profile identification as the returning-user path
           above — login-time identify(), not an event payload (#966). */
        email: userData.email,
        role: userData.role,
        name: userData.name,
      });
    }

    // LinkedIn conversion tracking - track signup/login as conversion
    // Only for real users (not guests), and only if lintrk is loaded
    if (window.lintrk && userData?.email && !userData?.is_guest) {
      try {
        // Track signup conversion - LinkedIn will match via their enhanced matching
        window.lintrk('track', { conversion_id: 23402932 });
        console.log('📊 LinkedIn: signup conversion tracked');
      } catch (e) {
        // Silently fail - analytics shouldn't break the app
        console.debug('LinkedIn tracking skipped:', e.message);
      }
    }
  };

  /**
   * @param {{expectedToken?: string|null}} [options]
   *   #3636 item 4: the token the caller means to end. The degraded panel's
   *   "Sign in again" passes `degradedToken`: the token its restore read, but
   *   only if it is this tab's own (null for a token adopted from another
   *   tab, TP-3636-2), so an adopted session always lands in the branch
   *   below. If storage holds something else by now (another tab signed in), that
   *   session was never this tab's: it is not revoked on the server and not
   *   cleared. One restore runs instead, and resolves to
   *   `{ superseded: true, status }` so the caller does not navigate away
   *   from a session it just adopted. Omitted: the pre-#3636 behaviour,
   *   revoke whatever is stored.
   */
  const logout = async ({ expectedToken } = {}) => {
    cancelRestoreRetry();
    if (expectedToken !== undefined && AuthAPI.getStoredToken() !== expectedToken) {
      const status = await restoreSession({ attempt: RESTORE_RETRY_DELAYS_MS.length });
      return { superseded: true, status };
    }
    // PR #3615 review-fix (codex P1): the token this logout revokes. Read in
    // the same synchronous tick as AuthAPI.logout's own read, so the two are
    // the same value. AuthAPI.logout only clears storage when it still holds
    // this token (#3580 7b), so a DIFFERENT token found after the await is
    // another tab's sign-in, written during the revoke round trip. That
    // tab's 'storage' event reached this tab while it was still
    // authenticated. Since #3636 the listener does act in that state (it
    // drops the shown user and restores), but the logout's own finally then
    // writes ANONYMOUS over it, so the replacement restore below is still the
    // one that adopts the other tab's session: it takes the newest
    // generation and supersedes the listener's.
    const revokedToken = AuthAPI.getStoredToken();
    try {
      // #2684: AuthAPI.logout() returns {ok, status} rather than throwing on
      // a server-side failure (it already catches internally so the local
      // session always clears via `finally` below). Surface a non-2xx here
      // too, at the boundary the learner-facing flow actually goes through,
      // rather than relying solely on the lower-level warning inside
      // AuthAPI.logout — a failed server revocation must be visible, never a
      // silent no-op that reads exactly like a successful one.
      const result = await AuthAPI.logout();
      if (result && result.ok === false) {
        console.warn(`[AUTH_LOGOUT] Server did not confirm logout (status ${result.status}); local session cleared regardless`);
      }
    } finally {
      verifiedTokenRef.current = null;
      verifiedUserIdRef.current = null;
      verifiedUserOrgIdRef.current = null;
      ownTokenRef.current = null;
      setDegradedToken(null);
      setUser(null);
      setStatus(AUTH_STATUS.ANONYMOUS);

      // #4010: drop the in-memory claim map (learner-specific text) with the session.
      clearClaimMapSession();

      // Reset PostHog user identity on logout
      if (posthog) {
        posthog.reset();
      }

      // PR #3615 review-fix: a replacement session survived the logout's
      // compare-and-swap, so verify it now instead of leaving this tab
      // anonymous beside a valid token until a reload (where guest entry
      // would also refuse with REAL_SESSION_UNVERIFIED). The same one-shot
      // restore the storage listener runs: no background retries, and it
      // clears the token only on a 401 for that same token. Not awaited:
      // the caller's navigation must not wait on /auth/me, and
      // restoreSession always settles (it never rejects).
      const replacement = AuthAPI.getStoredToken();
      if (replacement && replacement !== revokedToken) {
        restoreSession({ attempt: RESTORE_RETRY_DELAYS_MS.length });
      }
    }
    return { superseded: false };
  };

  const requestMagicLink = async (email) => {
    return AuthAPI.requestMagicLink(email);
  };

  const verifyMagicLink = async (token, email) => {
    const response = await AuthAPI.verifyMagicLink(token, email);
    if (response.success) {
      await login(response.token, response.user);
    }
    return response;
  };

  /**
   * Create a guest session for demo (Issue #282)
   * Returns the session data including voice budget info
   */
  // #3753: `scenarioId` is the scenario the caller is about to start a run
  // on. It overrides the URL's entry scenario for BOTH the org prediction and
  // the mint, so a run started from "/" or /discover (no scenario in the URL)
  // is judged by the scenario it will actually run, not by the page.
  const loginAsGuest = async (opts) => {
    const startScenarioId = (opts && typeof opts === 'object') ? opts.scenarioId : null;
    // #3578 (Gilbert, 2026-09-29): a browser that cannot store a session gets
    // a clear stop HERE, before AuthAPI.createGuestSession mints a server
    // guest row it could never keep. Without this, every retry minted another
    // guest row (burning the guest rate limit) and then threw at storeToken.
    //
    // #3636 item 3: Quota is not Security. A BLOCKED store can hold nothing,
    // so it stops here. A FULL store still holds its current guest token,
    // which the reuse path below resumes without writing it again (login()
    // skips an identical write), so it does not stop here; it is re-probed
    // with a token-sized value before a FRESH mint instead (below), which is
    // where a full store would otherwise mint a server row and then throw
    // QuotaExceededError at storeToken.
    const storageFailure = getAuthStorageFailure();
    if (storageFailure === 'security') {
      throw storageBlockedError('guest_entry');
    }

    // #3406: never mint a guest token over a stored REAL session. Every
    // caller reaches here only while `isAuthenticated` is false, which a
    // real learner's session also is while it is `degraded` (restore could
    // not reach /auth/me) or before another tab's login has been picked up.
    // Minting then overwrote the real token with a guest one. Refuse
    // instead: the stored token is still the learner's, and whatever they
    // do next with it (e.g. Start Practice) is sent as THEM; if it is truly
    // dead, that request's 401 clears it the normal way.
    //
    // Scoped to NON-guest tokens on purpose: a stored guest token — even a
    // stale or revoked one (docs/subsystems/auth.md gotcha 0) — still takes
    // the reuse-or-mint path below exactly as before, so a guest holding a
    // dead guest token is never stranded. An expired token is not a
    // session at all and does not block.
    const heldToken = AuthAPI.getStoredToken();
    if (isUsableRealSessionToken(heldToken)) {
      throw codedError(
        'A signed-in session is stored on this device but could not be verified yet.',
        'REAL_SESSION_UNVERIFIED',
      );
    }

    // #3578 H (plan check design change 3): the check above is
    // check-then-act. The mint below is a network round trip (and the reuse
    // validation another), so a REAL login completed in another tab inside
    // that window used to be overwritten by this guest's login(). So before
    // acting, storage is re-read and the flow aborts only if it now holds an
    // unexpired, non-guest token. That is exactly "a DIFFERENT real session
    // than this flow expected": the flow only gets here holding no usable
    // real token (the check above throws otherwise), and its own writes are
    // a clear to null or a guest token. So it needs no expected-value
    // bookkeeping, and the flow's own clear of a stale guest (a returning
    // guest revoked by an upgrade or merge) can never trip it, which an
    // "any change since entry aborts" rule would. Guest-to-guest stays last
    // write wins (the residual PR #2683's review accepted). The abort THROWS,
    // because every caller treats a resolved loginAsGuest as "session
    // established" (the #3011 contract below).
    const assertNoNewRealSession = () => {
      if (isUsableRealSessionToken(AuthAPI.getStoredToken())) {
        throw codedError(
          'Someone signed in on this device while we were starting your practice session.',
          'REAL_SESSION_UNVERIFIED',
        );
      }
    };

    // #2050: resolved here rather than by each caller — guest sessions are
    // started from many different components (HomePage, ScenarioDetailPage,
    // AuthoringDemoDialog, HomePageV2, and the guest-discovery pages) and
    // every one of them wants the same answer. Threading it through the call
    // sites would guarantee one gets missed and silently drops a partner's
    // guests back into `trial`.
    const entryKey = getGuestEntryKey();

    // #2673 — reuse a stored guest session instead of minting a fresh one,
    // resolved here for the same reason as the org routing above: every
    // caller of loginAsGuest wants "don't create a new identity if I already
    // have one for this landing" without knowing anything about how that's
    // decided. Reuse requires ALL of: an entry key for this landing (no
    // signal at all always mints, unchanged from before), a stored token
    // minted through the SAME entry key (see getGuestEntryKey's doc comment
    // for why that's the right proxy for "same org" without a second
    // network round trip), and that token still passing server-side
    // validation right now.
    //
    // #3599: the entry-key condition is not the only reason to re-check. The
    // degraded panel's "Go to home page" leaves the learner on "/" with the
    // guest token still stored, and "/" retains no audience or scenario key
    // for most arrivals, so a Start there reached this function with
    // `entryKey` null and minted a NEW guest over the stored one: the #3580
    // loss, with no warning. So a stored, unexpired guest token that THIS tab
    // has not verified (status degraded / loading / anonymous, or a token the
    // tab did not verify) is always re-checked first, whatever the entry key:
    // reused if the server confirms it, a network-shaped failure THROWS below
    // (never mints), and only a confirmed rejection reaches the mint. A
    // client-expired token is not a candidate (restore clears those too), and
    // with no stored token nothing here applies.
    const heldIsUnexpiredGuest =
      !!heldToken &&
      !AuthAPI.isTokenExpired(heldToken) &&
      decodeTokenPayload(heldToken)?.is_guest === true;
    const heldVerifiedHere =
      (authStatusRef.current === AUTH_STATUS.GUEST || authStatusRef.current === AUTH_STATUS.AUTHENTICATED) &&
      verifiedTokenRef.current === heldToken;
    const storedGuestUnverified = heldIsUnexpiredGuest && !heldVerifiedHere;

    // #3753 (Gilbert, 2026-10-20: "Fresh guest on mismatch"): a guest is
    // reused only while it is in the org a FRESH guest minted through this
    // same entry would land in. Pathway inference at run creation is scoped
    // by the guest's org, so a Yoma guest starting a public trial scenario
    // got a run with no pathway: no memory carry, no next stage, silently.
    // The prediction takes exactly the inputs the mint below sends, so the
    // two cannot disagree (else every landing would re-mint). An unknown
    // prediction keeps the current guest; only a known, different org mints.
    const overrideScenarioId = Number(startScenarioId);
    const mintEntry = {
      audience: getLandingAudience(),
      scenarioId: Number.isInteger(overrideScenarioId) && overrideScenarioId > 0
        ? overrideScenarioId
        : getEntryScenarioId(),
    };
    const predictTargetOrgId = async () => {
      const cacheKey = guestOrgEntryCacheKey(mintEntry);
      if (guestOrgPredictionRef.current.has(cacheKey)) return guestOrgPredictionRef.current.get(cacheKey);
      const predicted = await AuthAPI.resolveGuestOrganization(mintEntry);
      if (!predicted.ok) return null;
      guestOrgPredictionRef.current.set(cacheKey, predicted.organizationId);
      return predicted.organizationId;
    };
    // Set when a WORKING guest is being replaced because of a mismatch. If
    // the fresh mint then fails (rate limit, storage, network), that guest is
    // resumed rather than leaving the learner with no session: a run in the
    // wrong org is today's behaviour, a blocked Start is worse.
    let mismatchFallback = null;

    // #3753: callers used to skip loginAsGuest entirely for a tab already
    // verified as a guest (`if (!isAuthenticated)`), which is how the
    // mismatched guest reached run creation. They now call it for guests
    // too, so a verified guest lands here: same org (or unknown) is a no-op
    // that writes nothing; a mismatch goes to the mint below. Without this
    // branch the entry-key reuse below would mint a new guest for every
    // trial scenario a trial guest opens (each has its own entry key),
    // breaking the memory carry it exists for.
    const heldVerifiedGuestHere = heldIsUnexpiredGuest && heldVerifiedHere &&
      authStatusRef.current === AUTH_STATUS.GUEST;
    if (heldVerifiedGuestHere) {
      const heldOrgId = verifiedUserOrgIdRef.current;
      if (heldOrgId == null) {
        // Would make the check silently inert: /auth/me and the mint both
        // return organization_id today, so this is a contract break to see.
        console.warn('[GUEST_ORG_MISMATCH] verified guest has no organization_id; keeping it');
      }
      const targetOrgId = await predictTargetOrgId();
      // Re-read after the await: another tab may have replaced or cleared the
      // session meanwhile. Then this verified guest is no longer the one to
      // judge, so nothing is minted over whatever is there now.
      const stillHeld = AuthAPI.getStoredToken() === heldToken && verifiedTokenRef.current === heldToken;
      if (!stillHeld || decideGuestOrgReuse({ guestOrgId: heldOrgId, targetOrgId }) === 'reuse') {
        return { success: true, token: AuthAPI.getStoredToken(), reused: true, alreadyActive: true };
      }
      // #3753 review round 2 (P2): a guest that captured an email is kept
      // (see decideGuestOrgReuse). Asked of the server, not of `user` state:
      // the capture can happen in another tab, or fail its send after the
      // server already recorded it, and neither updates this tab's state.
      // Only reached on a mismatch, so a same-org landing costs no request.
      // A network-shaped failure is an unknown and keeps the guest; a 401/403
      // means the server no longer honours this guest, so there is nothing
      // left to protect and the mint goes ahead.
      const heldFresh = await AuthAPI.validateGuestSession(heldToken);
      const stillHeldAfterCheck = AuthAPI.getStoredToken() === heldToken && verifiedTokenRef.current === heldToken;
      if (!stillHeldAfterCheck) {
        return { success: true, token: AuthAPI.getStoredToken(), reused: true, alreadyActive: true };
      }
      if (heldFresh.networkError) {
        console.warn('[GUEST_ORG_MISMATCH] could not check the verified guest for a captured email (network); keeping it');
        return { success: true, token: heldToken, reused: true, alreadyActive: true };
      }
      if (decideGuestOrgReuse({
        guestOrgId: heldOrgId,
        targetOrgId,
        guestHasCapturedEmail: heldFresh.ok && heldFresh.user?.has_pending_email === true,
      }) === 'reuse') {
        console.info(`[GUEST_ORG_MISMATCH] verified guest in org ${heldOrgId} has a captured email; keeping it (this run's pathway is not inferred for org ${targetOrgId})`);
        return { success: true, token: heldToken, reused: true, alreadyActive: true, keptForCapturedEmail: true };
      }
      console.info(`[GUEST_ORG_MISMATCH] verified guest in org ${heldOrgId}; this entry mints into org ${targetOrgId}, minting a fresh guest`);
      mismatchFallback = { token: heldToken, user: null, alreadyActive: true };
    }

    if (!mismatchFallback && (entryKey || storedGuestUnverified)) {
      const storedEntryKey = getStoredGuestEntryKey();
      const storedToken = AuthAPI.getStoredToken();
      const isReuseCandidate =
        (storedGuestUnverified || (!!entryKey && storedEntryKey === entryKey)) &&
        !!storedToken &&
        !AuthAPI.isTokenExpired(storedToken) &&
        decodeTokenPayload(storedToken)?.is_guest === true;

      if (isReuseCandidate) {
        // Server-side validation, not just a client-side decode — the
        // upgrade/merge paths (docs/subsystems/auth.md gotcha 0) make an
        // old guest token invalid by design (401 TOKEN_REVOKED) in ways a
        // client can never detect from the JWT alone. `validateGuestSession`
        // deliberately avoids authFetch's global session-expired dispatch:
        // a stale guest token here is an expected, silent fallback, not a
        // real "your session expired" event for the visitor to see.
        // #3753: the org prediction runs alongside the validation (it never
        // throws; a failure is "unknown" and keeps the guest).
        const [validated, targetOrgId] = await Promise.all([
          AuthAPI.validateGuestSession(storedToken),
          predictTargetOrgId(),
        ]);

        // Compare-and-swap BEFORE acting on the validation result (review
        // catch, second Codex lane on #2683 — extends the CAS below to the
        // SUCCESS branch too). `validateGuestSession` is a network
        // round-trip, a far larger race window than the synchronous checks
        // elsewhere in this function: without this guard, a slow guest
        // revalidation that resolves `ok:true` could silently call
        // `login(storedToken, ...)` and overwrite whatever another tab
        // wrote to shared storage in the meantime — including a REAL
        // user's own login/upgrade token completed in another tab during
        // this same window. That crosses exactly the privilege boundary
        // the accepted guest-to-guest "last write wins" residual below
        // (and in PR #2683's review) explicitly does not cover. If storage
        // no longer holds the token we validated, abandon this reuse
        // attempt entirely — for either outcome — and fall through to the
        // normal mint path, untouched, exactly as if validation had failed.
        if (AuthAPI.getStoredToken() === storedToken) {
          // #3753 review round 2 (P2): a guest that captured an email is
          // reused even across orgs (see decideGuestOrgReuse).
          const validGuest = validated.ok && validated.user?.is_guest;
          const orgMismatch = validGuest &&
            decideGuestOrgReuse({
              guestOrgId: validated.user.organization_id,
              targetOrgId,
              guestHasCapturedEmail: validated.user.has_pending_email === true,
            }) === 'mint';
          if (validGuest && !orgMismatch && validated.user.has_pending_email === true &&
            decideGuestOrgReuse({ guestOrgId: validated.user.organization_id, targetOrgId }) === 'mint') {
            console.info(`[GUEST_ORG_MISMATCH] stored guest in org ${validated.user.organization_id} has a captured email; reusing it (this run's pathway is not inferred for org ${targetOrgId})`);
          }
          if (orgMismatch) {
            // #3753: a valid guest, but in another org than this entry mints
            // into. Not cleared: the mint below overwrites it on success, and
            // it is resumed if that mint fails.
            console.info(`[GUEST_ORG_MISMATCH] stored guest in org ${validated.user.organization_id}; this entry mints into org ${targetOrgId}, minting a fresh guest`);
            mismatchFallback = { token: storedToken, user: validated.user, voiceBudget: validated.voiceBudget, alreadyActive: false };
          } else if (validated.ok && validated.user?.is_guest) {
            await login(storedToken, validated.user);

            // `days_since_first`: the guest ROW's created_at IS the first-seen
            // timestamp for this identity — this is the same identity being
            // reused, not re-derived from a separate localStorage write.
            const createdAt = validated.user.created_at ? new Date(validated.user.created_at) : null;
            const daysSinceFirst = createdAt && !Number.isNaN(createdAt.getTime())
              ? Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / 86400000))
              : null;
            if (posthog) {
              posthog.capture('guest_return_visit', {
                days_since_first: daysSinceFirst,
                scenario_id: getEntryScenarioId(),
              });
            }

            return {
              success: true,
              token: storedToken,
              user: validated.user,
              voice_budget: validated.voiceBudget,
              reused: true,
            };
          }

          // #3011 parallel callsite: `validateGuestSession` collapses a
          // network failure, a cancelled fetch, a 5xx or a 429 into the same
          // `{ok:false}` shape a real 401/403 rejection returns (same root
          // cause as the initAuth catch above) — EXCEPT it also sets
          // `networkError: true` on exactly that ambiguous case (see
          // services/auth.js `validateGuestSession`'s catch block). Only a
          // confirmed rejection means the token is actually invalid; a
          // network-shaped failure tells us nothing about the token, so it
          // must not be treated as revocation. Leave the stored token and
          // entry key untouched, and abandon this reuse+mint attempt rather
          // than silently replacing a possibly-still-valid guest identity —
          // the next call (e.g. a retry, or the caller's own next request)
          // gets another chance to actually ask the server.
          //
          // review-fix (#3011 P1 codex): THROW here rather than resolving
          // to a false-ish `{success:false, ...}` object. Every caller of
          // `loginAsGuest` (HomePage, HomePageV2, startGuestRun,
          // AuthoringDemoDialog, GatheringChat, ScenarioDetailPage, the
          // guest-discovery pages) does `await loginAsGuest()` and then
          // unconditionally proceeds as an authenticated guest — none of
          // them inspect the resolved value's `success`/`networkError`
          // fields, because the ONLY other failure path through this
          // function (the mint call a few lines below, via
          // `AuthAPI.createGuestSession`) already throws on failure, so a
          // resolved promise has always meant "context established" by
          // convention. A resolved-but-unauthenticated return breaks that
          // invariant silently: e.g. `startGuestRun` (lib/discovery/
          // startGuestRun.js) awaits `loginAsGuest()` and then immediately
          // POSTs `/api/runs` and navigates to `/chat/:runId` regardless —
          // if connectivity recovers in time for that POST to succeed, a
          // real run gets created while `isAuthenticated` is still false,
          // and `ProtectedRoute` then bounces the freshly created run to
          // `/login`, a user-visible dead end. Throwing restores the
          // established contract; every caller already has a try/catch (or
          // an un-awaited rejection surfaces visibly) around this call.
          if (!orgMismatch && validated.networkError) {
            const err = new Error('Unable to validate guest session (network error)');
            err.networkError = true;
            throw err;
          }

          // Token invalid/revoked (401/TOKEN_REVOKED) — respect that by
          // design (gotcha 0: upgrade/merge deliberately make old guest
          // tokens invalid) and fall through to a fresh mint below, exactly
          // as if there had been no stored token at all.
          //
          // Compare-and-swap before clearing (review catch, #2683): two tabs
          // sharing this browser's localStorage can both reach this branch
          // for the same stale token at nearly the same time. Without this
          // check, a slower tab's clear/mint sequence can wipe out storage
          // AFTER a faster tab has already minted a fresh, valid guest and
          // written its token — silently invalidating a session that was
          // never actually stale. Only clear if the token we just validated
          // is still the one in storage (guaranteed true here, since the
          // outer check above just confirmed it); the guard is kept as its
          // own explicit statement so this branch reads correctly if the
          // outer condition's scope ever changes.
          if (!orgMismatch) {
            AuthAPI.clearStoredToken();
            storeGuestEntryKey(null);
          }
        } else {
          // Storage changed under us during the validation round-trip. A
          // guest token there is left for this call's own mint to overwrite
          // (the "last write wins" behaviour the unconditional pre-#2673
          // always-mint flow already had, and which PR #2683's review
          // accepted as a guest-to-guest-only residual risk). A REAL session
          // there is another tab's login: #3578 H, never overwrite it.
          assertNoNewRealSession();
        }
      }
    }

    const mintFreshGuest = async () => {
      // #3636 item 3: the mint-then-throw leak on a full store. Reuse did not
      // happen (or cleared a revoked token, which may have made room), so the
      // new token has to be written: check a token-sized write fits first.
      if (storageFailure === 'quota' && getAuthStorageFailure(GUEST_TOKEN_PROBE_BYTES) !== null) {
        throw storageBlockedError('guest_entry', undefined, 'quota');
      }

      const response = await AuthAPI.createGuestSession({
        audience: mintEntry.audience,
        scenarioId: mintEntry.scenarioId,
        // #3530 (D-185 b): where the visitor entered from, captured at BOOT (the
        // query string is usually gone by now). Always an explicit object in a
        // browser, even an empty one, so the server can tell "arrived direct"
        // from "nothing captured".
        entrySource: getEntrySource(),
      });
      if (response.success) {
        // #3578 H: compare-and-swap immediately before login() writes storage.
        // Synchronous from here into login()'s storeToken, so no await opens
        // a window between the check and the write in this tab.
        assertNoNewRealSession();
        await login(response.token, response.user);
        // Recorded on MINT only, never on reuse — the entry key persists for
        // as long as this guest identity is valid, so a later landing through
        // the same door can find it again.
        storeGuestEntryKey(entryKey);
        // #3753: the mint's own org is the prediction for its entry.
        const mintedOrgId = Number(response.user?.organization_id);
        if (Number.isInteger(mintedOrgId) && mintedOrgId > 0) {
          guestOrgPredictionRef.current.set(guestOrgEntryCacheKey(mintEntry), mintedOrgId);
        }
      }
      return response;
    };

    if (!mismatchFallback) return mintFreshGuest();

    try {
      return await mintFreshGuest();
    } catch (err) {
      // #3753: the fresh mint failed. Resume the working guest it was meant
      // to replace, but only if storage still holds it (compare-and-swap, as
      // everywhere above) and never across a real session that appeared in
      // the meantime (#3578 H: that throw is re-raised, not swallowed).
      if (err?.code === 'REAL_SESSION_UNVERIFIED') throw err;
      if (AuthAPI.getStoredToken() !== mismatchFallback.token) throw err;
      console.warn(`[GUEST_ORG_MISMATCH] fresh guest mint failed (${err?.code || err?.name || 'Error'}); resuming the existing guest`);
      if (!mismatchFallback.alreadyActive) {
        await login(mismatchFallback.token, mismatchFallback.user);
      }
      return {
        success: true,
        token: mismatchFallback.token,
        user: mismatchFallback.user,
        voice_budget: mismatchFallback.voiceBudget,
        reused: true,
        orgMismatchFallback: true,
      };
    }
  };

  /**
   * Sign in with Google OAuth (Issue #299)
   * @param {string} credential - Google ID token from Google Sign-In
   */
  const loginWithGoogle = async (credential) => {
    // #3475 Q2881-4: the token that nominates the guest is captured ONCE, here,
    // and every call of the round trip sends this same value (see
    // AuthAPI.loginWithGoogle's `bearerToken`). In one tab nothing writes
    // storage between the calls (login() runs only on the final response),
    // so this is exactly what the old per-call re-read sent.
    const nominatedToken = AuthAPI.getStoredToken();
    let response = await AuthAPI.loginWithGoogle(credential, { bearerToken: nominatedToken });

    // D-121 (#2725): the nominated guest session is older than the 24h
    // window, and the account can be created/signed-in right away either
    // way — this ONLY decides whether that guest's history comes along.
    // Decided here so all three callers (LoginPage, LoginModal, VerifyPage)
    // get the same behaviour without each re-implementing it.
    //
    // #3459 Q2881-1: a LOOP, not a single `if`. The server answers
    // pending_confirmation again whenever the answer cannot be applied (the
    // 10-minute confirm token expired while the dialog was open). The old
    // single `if` then fell straight through to `response.success` — the
    // ask response is `success: true` — and signed the learner in with the
    // merge silently skipped. A repeated ask is shown again, up to
    // MAX_GUEST_CONVERSION_PROMPTS; past that the flow STOPS with a coded
    // error. A still-pending response never reaches login() below.
    //
    // #3570: each outcome of the prompt is a PostHog `guest_conversion_prompt`
    // event, {outcome: yes | no | session_changed | unresolved, prompts}, so
    // the two browser-only stops are visible on prod (reader: DC-032, the
    // dated prod read due 2026-10-26). `prompts` is how many times the
    // question has been shown in this sign-in. No identity in the properties.
    let prompts = 0;
    while (response.guest_conversion === 'pending_confirmation') {
      if (prompts >= MAX_GUEST_CONVERSION_PROMPTS) {
        captureAuthEvent('guest_conversion_prompt', { outcome: 'unresolved', prompts });
        throw codedError(
          'We could not confirm whether to keep your earlier practice session. Please sign in with Google again.',
          'GUEST_CONVERSION_UNRESOLVED',
        );
      }
      prompts += 1;
      const answeredYes = confirmGuestConversion(response.guest_conversion_pending);

      // #3475 Q2881-4 (mega115 plan check finding 6): `confirm()` blocks only
      // this tab. If another tab replaced the stored token while it was
      // open, the answer no longer clearly belongs to the guest the learner
      // was shown, so it is not sent at all.
      if (AuthAPI.getStoredToken() !== nominatedToken) {
        captureAuthEvent('guest_conversion_prompt', { outcome: 'session_changed', prompts });
        throw codedError(
          'Your sign-in changed in another tab while we were asking. Please sign in with Google again.',
          'GUEST_CONVERSION_SESSION_CHANGED',
        );
      }
      captureAuthEvent('guest_conversion_prompt', { outcome: answeredYes ? 'yes' : 'no', prompts });

      response = await AuthAPI.loginWithGoogle(credential, {
        bearerToken: nominatedToken,
        guestConversionAnswer: answeredYes ? 'yes' : 'no',
        // review-fix (P1 codex, #2725): echo this ask's token back so the
        // backend can tell the answer is for THIS ask — see
        // AuthService.readGuestConversionConfirmToken.
        guestConversionToken: response.guest_conversion_pending?.confirm_token || null,
      });
    }

    if (response.success) {
      await login(response.token, response.user);
    }
    return response;
  };

  /**
   * Update user fields in local state (Issue #719 — name editing)
   * @param {Object} fields - Fields to merge into the user object
   */
  const updateUser = (fields) => {
    setUser(prev => prev ? { ...prev, ...fields } : prev);
  };

  /**
   * #2540: the ONLY safe way for a login token to reach this app from
   * OUTSIDE React — specifically, the testing panel's per-card "Copy login
   * cmd" button, which copies a DevTools console snippet a human pastes by
   * hand. Exposed on `window` (below) rather than threaded through props
   * because the caller has no React context of its own at all.
   *
   * This is a DIFFERENT code path from the boot-time guard in `initAuth`
   * above, deliberately. `clearStoredToken()` on an expired token found at
   * BOOT is correct — that is a session that aged out naturally while
   * unused, and there is nothing to protect by keeping it. An expired token
   * arriving THIS way is different: it is a credential a human is actively
   * handing the app, most often because the panel's test-fixture JWT is
   * older than its TTL. Silently clearing storage here would destroy
   * whatever real session the tester already had — that IS the #2540 bug
   * (session 167, 2026-08-29: a stale panel login logged Gilbert out of his
   * own working session with no explanation). So this path never calls
   * storeToken/clearStoredToken on an expired token, and never reloads —
   * the existing session is left exactly as it was, and the failure is
   * surfaced loudly (console.error + alert, since there may be no
   * mounted UI on the current page that shows `sessionExpiredMessage`).
   */
  const testLogin = useCallback((token) => {
    if (AuthAPI.isTokenExpired(token)) {
      const exp = getTokenExpiry(token);
      const expiredAt = exp ? new Date(exp * 1000).toLocaleString() : 'an earlier time';
      const message = `Test login token expired at ${expiredAt}. Your current session was left untouched — click "Copy login cmd" again for a fresh one.`;
      console.error(`[testing-panel] ${message}`);
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(message);
      }
      return { ok: false, reason: 'expired', message };
    }
    AuthAPI.storeToken(token);
    window.location.reload();
    return { ok: true };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__tacitTestLogin = testLogin;
    return () => {
      delete window.__tacitTestLogin;
    };
  }, [testLogin]);

  const reportDegradedExit = useCallback(
    (properties) => captureAuthEvent('auth_restore_degraded_exit', properties),
    [],
  );

  const value = {
    user,
    isLoading,
    isAuthenticated,
    // #3406: the explicit state behind the two booleans above, plus the
    // learner's own retry from `degraded` (the other exit is logout()).
    authStatus,
    retryAuthRestore,
    // #3636 item 4: the token the DEGRADED restore read if it is this tab's
    // own (null otherwise, including a token adopted from another tab);
    // the degraded panel passes it to logout({ expectedToken }).
    degradedToken,
    // #3599: the degraded panel reports which exit the learner took, through
    // the same never-throws wrapper as the restore's own events. The panel
    // does not import PostHog itself, and (like ProtectedRoute's tests) a
    // mocked useAuth may not provide this, so the panel calls it optionally.
    reportDegradedExit,
    token: AuthAPI.getToken(),
    sessionExpiredMessage,
    login,
    logout,
    loginAsGuest,
    loginWithGoogle,
    requestMagicLink,
    verifyMagicLink,
    updateUser,
    clearSessionExpiredMessage,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;